import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Chip,
  Button,
  Textarea,
  Divider,
} from '@mui/joy';
import {
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  User as PersonIcon,
  XCircle as CancelIcon,
  Check as CheckIcon,
} from '@phosphor-icons/react';
import { useTaskComments, useCommentMutations } from '../../hooks/useComments';
import { useOrganizationUsers } from '../../hooks/useUsers';
import { CommentInput } from './CommentInput';
import type { TaskComment } from '../../types/tasks';

interface TaskCommentsProps {
  taskId: number;
  currentUsername?: string;
}

export const TaskComments: React.FC<TaskCommentsProps> = ({ taskId, currentUsername }) => {
  const { data: comments = [], isLoading, refetch, isError, hasNextPage, fetchNextPage, isFetchingNextPage, count } = useTaskComments(taskId);
  const { data: users = [] } = useOrganizationUsers();
  const { createComment, updateComment, deleteComment } = useCommentMutations();
  
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  // Render comment content with highlighted mentions
  const renderCommentContent = (content: string) => {
    const mentionRegex = /@(\w+)/g;
    const parts = content.split(mentionRegex);
    
    return parts.map((part, index) => {
      // Even indices are regular text, odd indices are usernames
      if (index % 2 === 0) {
        return <span key={index}>{part}</span>;
      } else {
        return (
          <Typography
            key={index}
            component="span"
            sx={{
              color: 'var(--brand-text, #7013f5)',
              fontWeight: 'md',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            @{part}
          </Typography>
        );
      }
    });
  };

  // Handle comment submission
  const handleCommentSubmit = async (content: string) => {
    await createComment.mutateAsync({ taskId, content });
    refetch();
  };

  // Handle edit start
  const handleEditStart = (comment: TaskComment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  // Handle edit save
  const handleEditSave = async (commentId: number) => {
    if (editContent.trim()) {
      await updateComment.mutateAsync({ 
        commentId, 
        content: editContent.trim(),
        taskId,
      });
      setEditingComment(null);
      setEditContent('');
      refetch();
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingComment(null);
    setEditContent('');
  };

  // Handle delete
  const handleDelete = async (commentId: number) => {
    if (confirm('Are you sure you want to delete this comment?')) {
      await deleteComment.mutateAsync({ commentId, taskId });
      refetch();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {(isError || createComment.isError || updateComment.isError || deleteComment.isError) && <Alert color="danger">Unable to complete the comment request. <Button onClick={() => refetch()}>Retry</Button></Alert>}
      <Typography level="body-xs">{comments.length} of {count ?? 0} activity entries</Typography>
      {hasNextPage && <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>Load older activity</Button>}
      {/* Comment input */}
      <CommentInput
        onSubmit={handleCommentSubmit}
        users={users}
        isSubmitting={createComment.isPending}
      />
      
      <Divider />
      
      {/* Comments list */}
      {comments.length === 0 ? (
        <Typography level="body-md" sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Stack spacing={2}>
          {comments.map((comment) => (
            <Card key={comment.id} variant="outlined" size="sm">
              <CardContent>
                <Stack spacing={1}>
                  {/* Comment header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon size={20} color="var(--joy-palette-text-secondary)" />
                      <Typography level="body-sm" fontWeight="md">
                        {comment.author_name}
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        {formatRelativeTime(comment.created_at)}
                      </Typography>
                      {comment.is_system_generated && (
                        <Chip size="sm" variant="soft" color="neutral">
                          System
                        </Chip>
                      )}
                    </Stack>
                    
                    {/* Actions for own comments - for now we'll check by username */}
                    {!comment.is_system_generated && currentUsername && 
                     comment.author_name.includes(currentUsername) && (
                      <Stack direction="row" spacing={0.5}>
                        {editingComment !== comment.id && (
                          <>
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => handleEditStart(comment)}
                            >
                              <EditIcon size={16} />
                            </IconButton>
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="danger"
                              onClick={() => handleDelete(comment.id)}
                            >
                              <DeleteIcon size={16} />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    )}
                  </Stack>
                  
                  {/* Comment content */}
                  {editingComment === comment.id ? (
                    <Stack spacing={1}>
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        minRows={2}
                        autoFocus
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={handleEditCancel}
                          startDecorator={<CancelIcon />}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEditSave(comment.id)}
                          loading={updateComment.isPending}
                          startDecorator={<CheckIcon />}
                        >
                          Save
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                      {renderCommentContent(comment.content)}
                    </Typography>
                  )}
                  
                  {/* Edit indicator */}
                  {comment.created_at !== comment.updated_at && editingComment !== comment.id && (
                    <Typography level="body-xs" color="neutral" fontStyle="italic">
                      (edited)
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
};